import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, MapPin, Calendar, Clock, ShieldCheck, User } from "lucide-react";

/** Reusable birth-details form used by both Kundali Generator + Nakshatra Finder.
 *  Emits an onSubmit(payload) where payload = { name, date:'YYYY-MM-DD', time:'HH:MM',
 *  time_approximate, latitude, longitude, timezone, place }
 */
export default function BirthDetailsForm({ onSubmit, busy = false, ctaLabel = "Calculate", tool = "kundali" }) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [ampm, setAmpm] = useState("AM");
  const [approx, setApprox] = useState(false);
  const [place, setPlace] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [chosen, setChosen] = useState(null);   // { latitude, longitude, timezone, display_name }
  const [showSug, setShowSug] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onPlaceChange = (v) => {
    setPlace(v);
    setChosen(null);
    if (timer.current) clearTimeout(timer.current);
    if (!v || v.trim().length < 2) { setSuggestions([]); return; }
    setSearchBusy(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geocode?q=${encodeURIComponent(v.trim())}`);
        setSuggestions(data.results || []);
        setShowSug(true);
      } catch { /* ignore */ }
      finally { setSearchBusy(false); }
    }, 350);
  };

  const pick = (s) => {
    setChosen(s);
    setPlace(s.display_name);
    setShowSug(false);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!dob) return alert("Please choose your date of birth.");
    if (!hour || !minute) return alert("Please enter your birth time.");
    if (!chosen) return alert("Please choose a birthplace from the suggestions.");
    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    if (Number.isNaN(h) || Number.isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59) {
      return alert("Enter a valid time (1-12 hours, 0-59 minutes).");
    }
    // 12-hour → 24-hour conversion
    if (ampm === "AM") { if (h === 12) h = 0; }
    else { if (h < 12) h = h + 12; }
    onSubmit({
      name: name.trim(),
      date: dob,
      time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      time_approximate: approx,
      latitude: chosen.latitude,
      longitude: chosen.longitude,
      timezone: chosen.timezone,
      place: chosen.display_name,
      tool,
    });
  };

  return (
    <form onSubmit={submit} data-testid="birth-form" className="rounded-3xl border border-foreground/10 bg-background p-6 md:p-8 space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-widest text-foreground/50 flex items-center gap-2"><User className="w-3 h-3" /> Name <span className="text-foreground/40 normal-case tracking-normal">(optional)</span></Label>
        <Input data-testid="bf-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Your name" className="rounded-full mt-2" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-foreground/50 flex items-center gap-2"><Calendar className="w-3 h-3" /> Date of birth *</Label>
          <Input data-testid="bf-dob" type="date" required
            value={dob} onChange={(e) => setDob(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            className="rounded-full mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-foreground/50 flex items-center gap-2"><Clock className="w-3 h-3" /> Exact birth time *</Label>
          <div className="mt-2 flex items-center gap-2">
            <Input data-testid="bf-hour" inputMode="numeric" required placeholder="HH" value={hour}
              onChange={(e) => setHour(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="rounded-full text-center w-16" />
            <span className="text-foreground/40">:</span>
            <Input data-testid="bf-min" inputMode="numeric" required placeholder="MM" value={minute}
              onChange={(e) => setMinute(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="rounded-full text-center w-16" />
            <Select value={ampm} onValueChange={setAmpm}>
              <SelectTrigger data-testid="bf-ampm" className="rounded-full w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AM">AM</SelectItem>
                <SelectItem value="PM">PM</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-foreground/60">
        <Checkbox id="bf-approx" checked={approx} onCheckedChange={(v) => setApprox(!!v)} data-testid="bf-approx" />
        <Label htmlFor="bf-approx" className="cursor-pointer">Birth time is approximate</Label>
      </div>

      <div ref={wrapRef} className="relative">
        <Label className="text-xs uppercase tracking-widest text-foreground/50 flex items-center gap-2"><MapPin className="w-3 h-3" /> Birthplace *</Label>
        <Input data-testid="bf-place" required
          value={place} onChange={(e) => onPlaceChange(e.target.value)}
          onFocus={() => suggestions.length && setShowSug(true)}
          placeholder="Start typing city, e.g. New Delhi"
          className={`rounded-full mt-2 ${chosen ? "border-emerald-500/60" : ""}`}
          autoComplete="off" />
        {searchBusy && <Loader2 className="w-4 h-4 animate-spin absolute right-4 top-9 text-foreground/40" />}
        {showSug && suggestions.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-2 rounded-2xl border border-foreground/15 bg-background shadow-lg overflow-hidden">
            {suggestions.map((s, i) => (
              <button type="button" key={i} onClick={() => pick(s)}
                data-testid={`bf-suggest-${i}`}
                className="w-full text-left px-4 py-2.5 hover:bg-[#D4AF37]/10 border-b border-foreground/5 last:border-0">
                <div className="text-sm font-medium truncate">{s.display_name}</div>
                <div className="text-[11px] text-foreground/50 truncate">
                  {s.latitude.toFixed(3)}, {s.longitude.toFixed(3)} · {s.timezone || "unknown tz"}
                </div>
              </button>
            ))}
          </div>
        )}
        {chosen && (
          <div className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400 flex items-center gap-2" data-testid="bf-chosen">
            <ShieldCheck className="w-3 h-3" /> {chosen.latitude.toFixed(4)}, {chosen.longitude.toFixed(4)} · {chosen.timezone || "auto tz"}
          </div>
        )}
      </div>

      <Button type="submit" size="lg" disabled={busy} data-testid="bf-submit"
        className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold py-6">
        {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating…</>) : ctaLabel}
      </Button>

      <p className="text-[11px] text-foreground/50 text-center leading-relaxed">
        Your birth details are used only to calculate your chart. Please review our{" "}
        <a href="/privacy" className="underline hover:opacity-80">Privacy Policy</a> before continuing.
      </p>
    </form>
  );
}
